// Dial pad (SPEC §19, 2026-10-01; owner: "a little phone call icon in the bottom right corner ... dial any number ...
// copy paste"). A round phone button bottom-right on every signed-in page, hidden while a call is up or its outcome is
// owed (the call bar and the wrap-up dock take over then). The pad: one box that takes typing, keypad clicks and
// pastes in any format; Enter calls. The number becomes the setter's own lead (POST /api/calls/manual) and is dialled
// through the same gated prepare as a queue lead (manualDial.ts). "My leads" lists the setter's dial-pad leads.
// Mounted once, next to the global call bar (web/src/phone/PhoneProvider.tsx).
// 2026-10-02 (PJ: "an option to just not select the campaign ... then select the number whether I want to use Atlanta
// number, Ontario number"): the campaign picker has "No campaign" (remembered per user), and "Call from" picks one of our
// outbound numbers for this call only (Automatic = the best local number). The server checks both.
// 2026-10-20 (PJ: "I entered a number into the dialer to call and they didn't answer. I wanted to call back right
// away"): a number called recently asks "Call this number again?" (Yes / No) instead of refusing; the pad keeps an amber
// note with "Call again" after a No. Hard blocks (DNC, hours, the 3-a-day cap...) stay red, as before.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Campaign } from '../../../src/shared/api';
import type { CallFromNumber, ManualLead, RecentNumber } from '../../../src/shared/api-manual';
import { errorMessage } from '../api';
import { navigate } from '../app';
import { fmtDateTime, timeAgo, toast } from '../components/ui';
import { Spinner, useExternal } from '../components/ui-extra';
import { useMe } from '../me';
import { phone } from '../phone/manager';
import { endpoints } from './endpoints';
import { DISPOSITION_META, fmtPhone, MEMBERSHIP_STATUS_LABELS, membershipTone } from './labels';
import { canLeaveLead } from './machine';
import { callFromPad, openFromPad, type PadOutcome } from './manualDial';
import {
  callFromLabel, callFromProblem, padChoiceKey, padRegion, parsePad, parsePadChoice, pressKey, resolvePadChoice, typed,
  type PadCampaign, type PadChoice, type PadNumber,
} from './padPhone';
import { getDialer, openMembership, openNoCampaign, rememberedCampaignId, subscribeDialer } from './store';

const subscribePhone = (fn: () => void) => phone.subscribe(fn);
const getPhone = () => phone.getState();
const EMPTY_PAD: PadNumber = parsePad('');
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

function readPadChoice(userId: number): PadChoice | null {
  try { return parsePadChoice(localStorage.getItem(padChoiceKey(userId))); } catch { return null; }
}
function rememberPadChoice(userId: number, c: PadChoice): void {
  try { localStorage.setItem(padChoiceKey(userId), String(c)); } catch { /* not remembered */ }
}
const countryOfE164 = (e164: string) => padRegion(e164.replace(/^\+1/, ''))?.country ?? null;

function PhoneIcon(props: { size?: number }) {
  const s = props.size ?? 24;
  // Material Icons "call" glyph (Apache-2.0).
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

type Problem = Exclude<PadOutcome, { kind: 'dialing' }>;
type Dialing = Extract<PadOutcome, { kind: 'dialing' }>;

export function DialPad() {
  const me = useMe();
  const ps = useExternal(subscribePhone, getPhone);
  const d = useExternal(subscribeDialer, getDialer);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'pad' | 'leads'>('pad');
  const [value, setValue] = useState('');
  const [pad, setPad] = useState<PadNumber>(EMPTY_PAD);
  const [bizName, setBizName] = useState('');
  const [contact, setContact] = useState('');
  const [picked, setPicked] = useState<PadChoice | null>(null); // what the setter chose in the picker this time
  const [numbers, setNumbers] = useState<CallFromNumber[] | null>(null);
  const [fromId, setFromId] = useState<number | null>(null);    // "Call from": null = Automatic; this call only
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentNumber[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [q, setQ] = useState('');
  const [leads, setLeads] = useState<ManualLead[] | null>(null);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  // Hidden during a call (inbound or outbound, preparing through wrap-up): the call bar and the dock take over.
  const hidden = !!ps.active || !!ps.incoming || !canLeaveLead(d.call);
  useEffect(() => { if (hidden && open) setOpen(false); }, [hidden]);

  useEffect(() => {
    if (!open) return;
    setProblem(null);
    endpoints.campaigns().then((all) => {
      setCampaigns(all.filter((c) => c.status === 'live' && (me.role === 'admin' || c.userIds.includes(me.id))));
      setCampaignsError(null);
    }, (e) => setCampaignsError(`Couldn't load your campaigns: ${errorMessage(e)}`));
    endpoints.recentNumbers().then(setRecent, () => setRecent([]));
    endpoints.callFromNumbers().then(setNumbers, () => setNumbers([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (view === 'pad' ? numberRef : searchRef).current?.focus();
  }, [open, view]);

  useEffect(() => {
    if (!open || view !== 'leads') return;
    let live = true;
    const t = setTimeout(() => {
      endpoints.manualLeads(q).then((rows) => { if (live) { setLeads(rows); setLeadsError(null); } },
        (e) => { if (live) setLeadsError(`Couldn't load your leads: ${errorMessage(e)}`); });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [open, view, q]);

  if (hidden) return null;

  const region = padRegion(pad.digits);
  const list: PadCampaign[] = (campaigns ?? []).map((c) => ({ id: c.id, name: c.name, country: c.country }));
  const stored = readPadChoice(me.id);
  const choiceFor = (country: 'US' | 'CA' | null): PadChoice =>
    resolvePadChoice(list, { picked, stored, current: d.campaignId, remembered: rememberedCampaignId(), country });
  const choice = choiceFor(region?.country ?? null);
  const chosen = typeof choice === 'number' ? list.find((c) => c.id === choice) ?? null : null;
  const mismatch = chosen && region?.country && chosen.country !== 'MIXED' && chosen.country !== region.country
    ? `${chosen.name} calls ${chosen.country === 'CA' ? 'Canada' : 'the US'} only; this is a ${region.country === 'CA' ? 'Canadian' : 'US'} number.` : null;
  const phoneWhy = phone.whyCantCall();
  const fromNumber = fromId !== null ? (numbers ?? []).find((n) => n.id === fromId) ?? null : null;
  const fromWhy = fromNumber ? callFromProblem(fromNumber, region?.country ?? null) : null;

  function close(): void {
    setOpen(false);
    setTimeout(() => fabRef.current?.focus(), 0);
  }

  function setNumber(next: PadNumber, shown: string, input?: HTMLInputElement | null): void {
    setPad(next);
    setValue(shown);
    if (input && input.value !== shown) input.value = shown; // keep the box in step when the state didn't change
    setProblem(null);
  }

  /** Campaign for a number we already know: no campaign when it was a no-campaign number, its own lead's campaign when
   *  I can call it, else the pad's choice for its country. */
  function campaignFor(e164: string, own: number | null, noCampaign: boolean): PadChoice {
    if (noCampaign) return 'none';
    if (own !== null && list.some((c) => c.id === own)) return own;
    return choiceFor(countryOfE164(e164));
  }

  async function call(e164: string, target: PadChoice, withNames: boolean): Promise<void> {
    if (busy) return;
    if (fromId !== null) {
      // The picked caller ID must be able to call this number (the server checks again and never trusts this).
      const why = fromNumber ? callFromProblem(fromNumber, countryOfE164(e164)) : 'it is no longer in the list';
      if (why) { setProblem({ kind: 'error', message: `Can't call from that number: ${why}. Pick another number or Automatic.` }); return; }
    }
    setBusy(true);
    setProblem(null);
    const out = await callFromPad({
      phone: e164, ...(target === 'none' ? { noCampaign: true } : { campaignId: target }),
      businessName: withNames && bizName.trim() ? bizName.trim() : null,
      contactName: withNames && contact.trim() ? contact.trim() : null,
    }, navigate, { callFromNumberId: fromId, onDialing: dialing });
    setBusy(false);
    if (out.kind === 'dialing') { dialing(out); return; }
    // 'confirm': "Call this number again?" is up over the pad; its Yes dials (and runs dialing()), No leaves the note.
    setProblem(out);
  }

  /** The call is starting on the dialer: say so, clear the pad and close it. */
  function dialing(out: Dialing): void {
    const who = out.created ? fmtPhone(out.item.business.phoneE164) : out.item.business.name;
    toast(out.item.noCampaign
      ? `Calling ${who} with no campaign. After the call, save it to My leads or not.`
      : out.created
        ? `Calling ${who}. Saved as your new lead in ${out.item.campaign.name}.`
        : `Calling ${who}. That number is already on file, so this call is logged on it.`, 'info');
    setNumber(EMPTY_PAD, '');
    setBizName('');
    setContact('');
    setFromId(null); // a picked number is for that call only
    setProblem(null);
    setOpen(false);
  }

  function enterCalls(e: KeyboardEvent): void {
    if (e.key === 'Enter') { e.preventDefault(); if (pad.e164) void call(pad.e164, choice, true); }
  }

  function onPanelKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  }

  if (!open) {
    return (
      <button ref={fabRef} type="button" class="vd-fab" aria-label="Open the dial pad" aria-expanded="false" aria-controls="vd-pad"
        title="Dial pad: call any number" onClick={() => { setView('pad'); setOpen(true); }}>
        <PhoneIcon />
      </button>
    );
  }

  return (
    <>
      <div id="vd-pad" class="vd-pad card" role="dialog" aria-label="Dial pad" onKeyDown={onPanelKey}>
        <div class="vd-pad-head">
          <div class="vd-pad-tabs" role="tablist" aria-label="Dial pad views">
            <button type="button" role="tab" aria-selected={view === 'pad'} class={view === 'pad' ? 'active' : ''} onClick={() => setView('pad')}>Dial</button>
            <button type="button" role="tab" aria-selected={view === 'leads'} class={view === 'leads' ? 'active' : ''} onClick={() => setView('leads')}>My leads</button>
          </div>
          <button type="button" class="btn btn-sm" onClick={close} aria-label="Close the dial pad">Esc</button>
        </div>

        {view === 'pad' ? (
          <div class="vd-pad-body">
            <label for="vd-pad-number" class="eyebrow">Phone number</label>
            <input id="vd-pad-number" ref={numberRef} class="vd-pad-number mono" type="tel" inputMode="tel" autoComplete="off" spellcheck={false}
              placeholder="Type or paste a number" value={value} aria-describedby="vd-pad-where"
              onInput={(e) => {
                const el = e.currentTarget;
                const deleting = ((e as InputEvent).inputType ?? '').startsWith('delete');
                const r = typed(pad, el.value, deleting);
                setNumber(r.pad, r.value, el);
              }}
              onPaste={(e) => {
                const text = e.clipboardData?.getData('text') ?? '';
                if (!text) return;
                e.preventDefault();
                const p = parsePad(text);
                setNumber(p, p.digits ? p.display : text.trim().slice(0, 80), e.currentTarget);
              }}
              onBlur={(e) => { if (pad.digits && e.currentTarget.value !== pad.display) setNumber(pad, pad.display, e.currentTarget); }}
              onKeyDown={enterCalls} />
            <div id="vd-pad-where" class="vd-pad-where" aria-live="polite">
              {pad.error ? <span class="vd-bad">{pad.error}</span>
                : region ? <span class={region.tone === 'bad' ? 'vd-bad' : region.tone === 'warn' ? 'vd-warn' : ''}>{region.text}</span>
                  : <span class="muted">Any format works: (404) 555-0123, 404.555.0123, +1 404 555 0123.</span>}
              {pad.e164 ? <span class="mono muted">{pad.e164}</span> : null}
            </div>

            <div class="vd-pad-keys" role="group" aria-label="Keypad">
              {KEYS.map((k) => (
                <button type="button" class="btn" aria-label={`Digit ${k}`} onClick={() => { const n = pressKey(pad, k); setNumber(n, n.display); numberRef.current?.focus(); }}>{k}</button>
              ))}
              <button type="button" class="btn vd-pad-key-soft" aria-label="Clear the number" onClick={() => { setNumber(EMPTY_PAD, ''); numberRef.current?.focus(); }}>Clear</button>
              <button type="button" class="btn" aria-label="Digit 0" onClick={() => { const n = pressKey(pad, '0'); setNumber(n, n.display); numberRef.current?.focus(); }}>0</button>
              <button type="button" class="btn vd-pad-key-soft" aria-label="Delete the last digit" onClick={() => { const n = pressKey(pad, '<'); setNumber(n, n.display); numberRef.current?.focus(); }}>⌫</button>
            </div>

            <div class="vd-pad-fields">
              <label>Business name
                <input type="text" value={bizName} maxLength={200} placeholder="Unknown business" autoComplete="off"
                  onInput={(e) => setBizName(e.currentTarget.value)} onKeyDown={enterCalls} />
              </label>
              <label>Contact name
                <input type="text" value={contact} maxLength={120} placeholder="Optional" autoComplete="off"
                  onInput={(e) => setContact(e.currentTarget.value)} onKeyDown={enterCalls} />
              </label>
              <label class="vd-pad-campaign">Campaign
                <select value={String(choice)} onChange={(e) => {
                  const v = e.currentTarget.value;
                  const c: PadChoice = v === 'none' ? 'none' : Number(v);
                  setPicked(c);
                  rememberPadChoice(me.id, c);
                  setProblem(null);
                }}>
                  <option value="none">No campaign (just call)</option>
                  {list.map((c) => <option value={c.id}>{c.name}</option>)}
                  {campaigns === null && !campaignsError ? <option value="" disabled>Loading campaigns…</option> : null}
                </select>
              </label>
              <label class="vd-pad-campaign">Call from
                <select value={fromId === null ? '' : String(fromId)} onChange={(e) => { const v = e.currentTarget.value; setFromId(v ? Number(v) : null); setProblem(null); }}>
                  <option value="">Automatic (best local number)</option>
                  {(numbers ?? []).map((n) => {
                    const why = callFromProblem(n, region?.country ?? null);
                    return <option value={n.id} disabled={!!why && n.id !== fromId} title={why ?? undefined}>{callFromLabel(n)}{why ? ` (${why})` : ''}</option>;
                  })}
                </select>
              </label>
            </div>
            {mismatch ? <p class="vd-pad-note">{mismatch}</p> : null}
            {fromWhy ? <p class="vd-pad-note vd-bad">Can't call from {fromNumber ? callFromLabel(fromNumber).split(' · ')[0] : 'that number'}: {fromWhy}. Pick another number or Automatic.</p> : null}
            {campaignsError ? <p class="vd-pad-note vd-bad">{campaignsError}</p> : null}

            <ProblemBox problem={problem} onOpen={(item) => { void openFromPad(item, navigate).then((ok) => { if (ok) setOpen(false); }); }} />
            {phoneWhy ? <p class="vd-pad-note">{phoneWhy}</p> : null}

            <button type="button" class="btn btn-primary vd-pad-call" disabled={!pad.e164 || busy || !!phoneWhy || !!fromWhy}
              onClick={() => { if (pad.e164) void call(pad.e164, choice, true); }}>
              {busy ? <><Spinner label="Checking" /> Checking the calling rules…</> : <><PhoneIcon size={18} /> Call {pad.e164 ? fmtPhone(pad.e164) : ''} <kbd>Enter</kbd></>}
            </button>
            <p class="vd-pad-note muted">{choice === 'none'
              ? 'No campaign: checked against every calling rule before it rings. After the call you save it to My leads or not.'
              : 'Saved as your own new lead, then checked against every calling rule before it rings.'}</p>

            {recent && recent.length ? (
              <div class="vd-pad-recent">
                <div class="eyebrow">Recent</div>
                <ul>
                  {recent.map((r) => (
                    <li>
                      <button type="button" class="vd-pad-row" disabled={busy || !!phoneWhy} aria-label={`Call ${r.businessName ?? ''} ${fmtPhone(r.phoneE164)} again`}
                        onClick={() => void call(r.phoneE164, campaignFor(r.phoneE164, r.campaignId, r.campaignId === null), false)}>
                        <span class="mono">{fmtPhone(r.phoneE164)}</span>
                        <span class="vd-pad-row-name">{r.businessName ?? ''}</span>
                        <span class="muted">{timeAgo(r.lastAt)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div class="vd-pad-body">
            <label for="vd-pad-search" class="eyebrow">My leads from the dial pad</label>
            <input id="vd-pad-search" ref={searchRef} type="search" value={q} placeholder="Search name, contact or number" autoComplete="off"
              onInput={(e) => setQ(e.currentTarget.value)} />
            {leadsError ? <p class="vd-bad">{leadsError}</p> : null}
            {leads === null && !leadsError ? <p class="muted"><Spinner /> Loading…</p> : null}
            {leads && !leads.length ? (
              <p class="muted">{q ? 'No match.' : 'Nothing yet. Every number you call from the pad is saved here as your own lead.'}</p>
            ) : null}
            {leads && leads.length ? (
              <ul class="vd-pad-leads">
                {leads.map((l) => (
                  <li>
                    <button type="button" class="vd-pad-lead" title="Open this lead on the dialer"
                      onClick={() => {
                        const opening = l.membershipId !== null ? openMembership(l.membershipId) : openNoCampaign(l.businessId);
                        void opening.then((r) => { if (r === 'opened' || r === 'busy') { navigate('/'); setOpen(false); } });
                      }}>
                      <span class="vd-pad-lead-name">{l.name}</span>
                      <span class="mono">{fmtPhone(l.phoneE164)} · {l.stateProv}</span>
                      <span class="vd-pad-lead-meta">
                        {l.status ? <span class={membershipTone(l.status) ? `chip chip-${membershipTone(l.status)}` : 'chip'}>{MEMBERSHIP_STATUS_LABELS[l.status]}</span>
                          : l.lastOutcome ? <span class="chip">{DISPOSITION_META[l.lastOutcome].label}</span> : null}
                        {l.blocked && l.status !== 'dnc' ? <span class="chip chip-bad">Never call</span> : null}
                        <span class="muted">{l.dmName ? `${l.dmName} · ` : ''}{l.campaignName ?? 'No campaign'} · {l.attempts} attempt{l.attempts === 1 ? '' : 's'} · added {timeAgo(l.createdAt)}</span>
                      </span>
                    </button>
                    <button type="button" class="btn btn-sm" disabled={busy || l.blocked || !!phoneWhy} aria-label={`Call ${l.name}`}
                      title={l.blocked ? 'This number must not be called' : 'Call this number again'}
                      onClick={() => { setView('pad'); void call(l.phoneE164, campaignFor(l.phoneE164, l.campaignId, l.membershipId === null), false); }}>Call</button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      <button ref={fabRef} type="button" class="vd-fab vd-fab-open" aria-label="Close the dial pad" aria-expanded="true" aria-controls="vd-pad" onClick={close}>
        <span aria-hidden="true" class="vd-fab-x">×</span>
      </button>
    </>
  );
}

function ProblemBox(props: { problem: Problem | null; onOpen: (item: NonNullable<Extract<Problem, { kind: 'blocked' }>['item']>) => void }) {
  const p = props.problem;
  if (!p) return null;
  if (p.kind === 'confirm') {
    // PJ 2026-10-20: a warning, not a block (amber): only the soft attempt rules stand in the way; Call again asks first.
    // PJ 2026-10-28: or a meeting booked, which reads "meeting booked" and Call anyway.
    const meeting = p.item.dialGate.confirm?.meeting === true;
    return (
      <div class="vd-pad-alert" role="note">
        <b>{p.item.business.name}: {meeting ? 'meeting booked.' : 'called recently.'}</b>
        <ul>{p.item.dialGate.reasons.map((r) => <li>{r.message}</li>)}</ul>
        <button type="button" class="btn btn-sm btn-primary" style="margin-top:6px" onClick={p.ask}>{meeting ? 'Call anyway' : 'Call again'}</button>
      </div>
    );
  }
  if (p.kind !== 'blocked') return <div class="vd-pad-alert" role="alert">{p.message}</div>;
  return (
    <div class="vd-pad-alert vd-pad-alert-bad" role="alert">
      <b>{p.businessName ? `${p.businessName}: not called.` : 'Not called.'}</b>
      <ul>{p.reasons.map((r) => <li>{r.message}</li>)}</ul>
      {p.retryAt ? <div>Earliest it could work: {fmtDateTime(p.retryAt)} (your time).</div> : null}
      {p.item ? (
        <button type="button" class="btn btn-sm" style="margin-top:6px" onClick={() => props.onOpen(p.item!)}>Open the lead (saved, not dialled)</button>
      ) : null}
    </div>
  );
}
